import { createFileRoute } from "@tanstack/react-router";
import {
  Button,
  FieldError,
  Form,
  Input,
  Label,
  TextField,
  toast,
} from "@heroui/react";
import { SubmitHandler, useForm } from "react-hook-form";

import {
  useTariffsConfigurationsById,
  useUpdateTariffsConfigurations,
} from "@/features/tariffs-configurations/use-tariffs-configurations.ts";

export const Route = createFileRoute(
  "/dashboard/tariff/configuration/$configurationId/edit",
)({
  component: RouteComponent,
});

interface IFormInput {
  title: string;
  isActive: boolean;

  costOfOneCourierVisit: number;
  fuelCostPerOrder: number;
  totalDepreciationPerOrder: number;
  tax: number;
  bankCommission: number;
  plannedMarkup: number;
}

function RouteComponent() {
  const { configurationId } = Route.useParams();
  const { data } = useTariffsConfigurationsById({ configurationId });
  const { mutateAsync, isPending } = useUpdateTariffsConfigurations({
    onSuccess: () => {
      toast.success("Данные обновлены");
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<IFormInput>();

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    mutateAsync({
      id: Number(configurationId),
      title: data.title.trim(),
      isActive: data.isActive,

      costOfOneCourierVisit: Number(data.costOfOneCourierVisit),
      fuelCostPerOrder: Number(data.fuelCostPerOrder),
      totalDepreciationPerOrder: Number(data.totalDepreciationPerOrder),
      tax: Number(data.tax),
      bankCommission: Number(data.bankCommission),
      plannedMarkup: Number(data.plannedMarkup),
    }).then(() => {
      reset();
    });
  };

  if (!data?.data) return null;

  return (
    <Form className="flex flex-col pt-4 pr-4" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-row gap-2 justify-between h-12 items-center w-full">
        <p className="font-bold text-xl">Редактировать базовую цену</p>

        <Button isPending={isPending} type="submit" variant="primary">
          Сохранить
        </Button>
      </div>

      <div className="container mt-6 gap-4 flex flex-col max-w-[40rem]">
        <TextField
          defaultValue={data.data.title}
          isInvalid={!!errors.title?.message}
        >
          <Label>Название</Label>
          <Input {...register("title", { required: "Нужно заполнить поле" })} />
          <FieldError>{errors.title?.message}</FieldError>
        </TextField>

        <TextField
          defaultValue={`${data.data.costOfOneCourierVisit}`}
          isInvalid={!!errors.costOfOneCourierVisit?.message}
        >
          <Label>Себестоимость одного выезда курьера</Label>
          <Input
            {...register("costOfOneCourierVisit", {
              required: "Нужно заполнить поле",
            })}
          />
          <FieldError>{errors.costOfOneCourierVisit?.message}</FieldError>
        </TextField>

        <TextField
          defaultValue={`${data.data.fuelCostPerOrder}`}
          isInvalid={!!errors.fuelCostPerOrder?.message}
        >
          <Label>Себестоимость топлива на 1 заказ</Label>
          <Input
            {...register("fuelCostPerOrder", {
              required: "Нужно заполнить поле",
            })}
          />
          <FieldError>{errors.fuelCostPerOrder?.message}</FieldError>
        </TextField>

        <TextField
          defaultValue={`${data.data.totalDepreciationPerOrder}`}
          isInvalid={!!errors.totalDepreciationPerOrder?.message}
        >
          <Label>Итоговая амортизация на заказ</Label>
          <Input
            {...register("totalDepreciationPerOrder", {
              required: "Нужно заполнить поле",
            })}
          />
          <FieldError>{errors.totalDepreciationPerOrder?.message}</FieldError>
        </TextField>

        <TextField
          defaultValue={`${data.data.tax}`}
          isInvalid={!!errors.tax?.message}
        >
          <Label>Налог в %</Label>
          <Input
            {...register("tax", {
              required: "Нужно заполнить поле",
            })}
          />
          <FieldError>{errors.tax?.message}</FieldError>
        </TextField>

        <TextField
          defaultValue={`${data.data.bankCommission}`}
          isInvalid={!!errors.bankCommission?.message}
        >
          <Label>Комиссия банка в %</Label>
          <Input
            {...register("bankCommission", {
              required: "Нужно заполнить поле",
            })}
          />
          <FieldError>{errors.bankCommission?.message}</FieldError>
        </TextField>

        <TextField
          defaultValue={`${data.data.plannedMarkup}`}
          isInvalid={!!errors.plannedMarkup?.message}
        >
          <Label>Плановая наценка в %</Label>
          <Input
            {...register("plannedMarkup", {
              required: "Нужно заполнить поле",
            })}
          />
          <FieldError>{errors.plannedMarkup?.message}</FieldError>
        </TextField>
      </div>
    </Form>
  );
}
